"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import Script from "next/script";
import { MOT_COOKIE_PREFERENCES_CHANGED } from "@/components/CookieConsent";
import { isAnalyticsAllowed } from "@/lib/analytics-consent";
import { isProdPublicTrackingEnabled } from "@/lib/public-deploy-env";
import { onAnalyticsAllowanceChanged } from "@/lib/vendor-tracking-lifecycle";

/**
 * Loads Microsoft Clarity only on production deploys and only when analytics is allowed (consent ∧ ¬GPC).
 */
export function ClarityLoader({ projectId }: { projectId: string | undefined }) {
  const [enabled, setEnabled] = useState(false);
  const [initGeneration, setInitGeneration] = useState(0);
  const wasEnabledRef = useRef(false);
  const prod = isProdPublicTrackingEnabled();
  const id = projectId?.trim() ?? "";

  const applyAllowance = useCallback((allowed: boolean) => {
    if (!allowed) {
      onAnalyticsAllowanceChanged(false);
      wasEnabledRef.current = false;
      setEnabled(false);
      return;
    }
    if (!wasEnabledRef.current) {
      setInitGeneration((generation) => generation + 1);
    }
    wasEnabledRef.current = true;
    setEnabled(true);
  }, []);

  const sync = useCallback(() => {
    applyAllowance(isAnalyticsAllowed());
  }, [applyAllowance]);

  useEffect(() => {
    if (!prod || !id) return;
    sync();
    function onPrefs() {
      applyAllowance(isAnalyticsAllowed());
    }
    window.addEventListener(MOT_COOKIE_PREFERENCES_CHANGED, onPrefs);
    return () => window.removeEventListener(MOT_COOKIE_PREFERENCES_CHANGED, onPrefs);
  }, [prod, id, sync, applyAllowance]);

  if (!prod || !id || !enabled) {
    return null;
  }

  const safeId = JSON.stringify(id);

  return (
    <Script id={`mot-clarity-init-${initGeneration}`} strategy="afterInteractive">
      {`(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})(window,document,"clarity","script",${safeId});`}
    </Script>
  );
}
